<script>
import axios from 'axios';
import DataTable from 'datatables.net-vue3';
import moment from 'moment';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Swal from 'sweetalert2';

export default {
    name: "MdmMainTable",
    components: {
        DataTable,
        flatPickr
    },
    props: {
        canExport: { type: Boolean, default: false }
    },
    data() {
        return {
            deviceList: [],
            loading: true,
            status: '',
            mdmStatus: '',
            dtKey: 0,
            isButtonDisabled: false,
            messageBag: [],
            
            // Export Feature States
            showExportModal: false,
            exportFilters: {
                dateRange: '',
                types: [1, 2, 3], // 1: Warning, 2: Enable Lost Mode, 3: Disable Lost Mode
                format: 'excel'
            },
            flatpickrConfig: {
                mode: 'range',
                dateFormat: 'Y-m-d',
            },

            options: {
                searching: true,
                columnDefs: [
                    { orderable: false, targets: [0, 1, 2, 3, 5] }, // Updated for Stats column
                ],
                // Default sorting by column 4 (MDM & Health) descending (latest sync first)
                order: [[4, 'desc']], 
                responsive: true
            },
        };
    },
    methods: {
        format_date(value) {
            if (value) {
                return moment(String(value)).format('DD-MM-YYYY HH:mm:ss')
            }
            return '---';
        },
        getDeviceList() {
            this.loading = true;
            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt')
            
            const params = {
                paginate: 0,
                enrollment_status: this.status,
                status: this.mdmStatus
            };

            axios.get(process.env.VUE_APP_API_BASEURL + 'mdm', { params })
                .then(response => {
                    if (response && response.data && response.data.data) {
                        this.loading = false;
                        
                        this.deviceList = response.data.data;

                        this.dtKey += 1;
                    } else {
                        this.loading = false;
                        this.deviceList = [];
                        this.messageBag = ['No record found.'];
                    }
                })
                .catch(error => {
                    this.loading = false;
                    this.messageBag = [error.response ? error.response.data.message : error.message];
                });
        },
        submitFilter(e) {
            e.preventDefault();
            this.isButtonDisabled = true;
            this.getDeviceList();
            this.isButtonDisabled = false;
        },
        resetFilter() {
            this.status = '';
            this.mdmStatus = '';
            this.getDeviceList();
        },

        // Export Modal Methods
        openExportModal() {
            this.showExportModal = true;
        },
        closeExportModal() {
            this.showExportModal = false;
        },
        handleExportDevices() {
            if (!this.exportFilters.dateRange) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Selection Required',
                    text: 'Please select a date range first.',
                    confirmButtonColor: '#3085d6'
                });
                return;
            }
            
            this.isButtonDisabled = true;

            // Extract enrolled_from and enrolled_to from date range string
            const dates = this.exportFilters.dateRange.split(' to ');
            const enrolled_from = dates[0];
            const enrolled_to = dates[1] || dates[0];

            const params = {
                enrollment_status: this.status || 'all',
                status: this.mdmStatus,
                enrolled_from,
                enrolled_to
            };

            axios.get(process.env.VUE_APP_API_BASEURL + 'mdm/export', { 
                params,
                responseType: 'blob' 
            })
            .then(response => {
                this.isButtonDisabled = false;
                
                // Construct download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `MDM_History_Export_${moment().format('DD-MM-YYYY_hhmmA')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                this.closeExportModal();

                Swal.fire({
                    icon: 'success',
                    title: 'Report Generated',
                    text: 'Your MDM device report has been downloaded.',
                    timer: 2000,
                    showConfirmButton: false
                });
            })
            .catch(error => {
                this.isButtonDisabled = false;
                console.error("Export Error:", error);

                Swal.fire({
                    icon: 'error',
                    title: 'Export Failed',
                    text: 'An error occurred while generating the report. Please try again.',
                    confirmButtonColor: '#f46a6a',
                });
            });
        },

        handleExportHistorical() {
            if (!this.exportFilters.dateRange) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Selection Required',
                    text: 'Please select a date range first.',
                    confirmButtonColor: '#3085d6'
                });
                return;
            }

            this.isButtonDisabled = true;

            // Extract requested_from and requested_to from date range string
            const dates = this.exportFilters.dateRange.split(' to ');
            const requested_from = dates[0];
            const requested_to = dates[1] || dates[0];

            const params = {
                requested_from,
                requested_to
            };

            axios.get(process.env.VUE_APP_API_BASEURL + 'mdm/command-logs/export', { 
                params,
                responseType: 'blob' 
            })
            .then(response => {
                this.isButtonDisabled = false;
                
                // Construct download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `MDM_Command_Logs_Export_${moment().format('DD-MM-YYYY_hhmmA')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                this.closeExportModal();

                Swal.fire({
                    icon: 'success',
                    title: 'History Report Generated',
                    text: 'Your MDM command history report has been downloaded.',
                    timer: 2000,
                    showConfirmButton: false
                });
            })
            .catch(error => {
                this.isButtonDisabled = false;
                console.error("Export Error:", error);

                Swal.fire({
                    icon: 'error',
                    title: 'Export Failed',
                    text: 'An error occurred while generating the history report. Please try again.',
                    confirmButtonColor: '#f46a6a',
                });
            });
        },

        /**
         * Returns a badge class based on MdmState enum:
         *   1 = Active, 2 = Warning, 3 = Lost Mode
         */
        mdmStateClass(status) {
            const map = {
                1: 'badge-soft-success', // Active
                2: 'badge-soft-warning', // Warning
                3: 'badge-soft-danger',  // Lost Mode
            };
            return map[status] ?? 'badge-soft-secondary';
        },

        /**
         * Returns a badge class based on MDM Profile Status:
         *   'enrolled' | 'removed' | 'unknown' | null
         */
        mdmProfileStatusClass(status) {
            const map = {
                'enrolled': 'badge-soft-success',
                'removed': 'badge-soft-danger',
                'unknown': 'badge-soft-secondary',
            };
            return map[status] ?? 'badge-soft-warning';
        },

        /**
         * Returns a label for MDM Profile Status
         */
        mdmProfileStatusLabel(status) {
            const map = {
                'enrolled': 'Enrolled',
                'removed': 'Removed',
                'unknown': 'Unknown',
            };
            return map[status] ?? 'Not Enrolled';
        },
    },
    mounted() {
        this.getDeviceList();
    }
};
</script>

<template>
    <div class="card">
        <div class="card-header border-bottom-dashed">
            <div class="d-flex align-items-center flex-wrap gap-2">
                <div class="flex-grow-1">
                    <form @submit.prevent="submitFilter" class="row g-1 align-items-center">
                        <div class="col-auto">
                            <select v-model="status" class="form-select form-select-sm" style="width: 180px;">
                                <option value="">Show All Devices</option>
                                <option value="linked">Linked to Contract</option>
                                <option value="not_linked">Not Linked to Contract</option>
                            </select>
                        </div>
                        <div class="col-auto">
                            <select v-model="mdmStatus" class="form-select form-select-sm" style="width: 110px;">
                                <option value="">Status</option>
                                <option value="1">Active</option>
                                <option value="3">Lost Mode</option>
                            </select>
                        </div>
                        <div class="col-auto">
                            <button type="submit" class="btn btn-primary btn-sm" :disabled="isButtonDisabled" title="Apply Filter">
                                <i class="ri-filter-2-line me-1 align-bottom"></i> Filter
                            </button>
                        </div>
                        <div class="col-auto">
                            <button type="button" @click='resetFilter' class="btn btn-light btn-sm" title="Reset Filters">
                                <i class="ri-refresh-line align-bottom"></i>
                            </button>
                        </div>
                    </form>
                </div>
                <div class="flex-shrink-0" v-if="canExport">
                    <button type="button" @click="openExportModal" class="btn btn-soft-success btn-sm px-3">
                        <i class="ri-file-excel-2-line me-1 align-bottom"></i> Export Report
                    </button>
                </div>
            </div>
        </div>
        <div class="card-body">
            <div v-if='loading' class="text-center p-5">
                <div class="spinner-border text-success" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
            <div v-else class="table-responsive">
                <DataTable :key="dtKey" :options="options" class="table table-hover table-nowrap align-middle mb-0"> 
                    <thead class="table-light">
                        <tr>
                            <th style="width: 30px;" class="text-center">No.</th>
                            <th>Device Details</th>
                            <th>Customer</th>
                            <th>Contract & Order</th>
                            <th>MDM Details</th>
                            <th>System Logs</th>
                        </tr>
                    </thead>
                    <tbody>                        
                        <tr v-for="(item, index) in deviceList" :key="index">
                            <td class="text-center text-muted">{{ index + 1 }}</td>
                            
                            <!-- Device Details -->
                            <td>
                                <div class="d-flex align-items-center">
                                    <div class="flex-grow-1">
                                        <h6 class="fs-14 mb-1">{{ item.device_details.product_name }}</h6>
                                        <div class="text-muted fs-12">
                                            SN: <span class="text-dark">{{ item.device_details.serial_number }}</span>
                                        </div>
                                        <div class="text-muted fs-12">
                                            IMEI 1: <span class="text-dark">{{ item.device_details.imei_1 }}</span>
                                        </div>
                                        <div class="text-muted fs-12">
                                            IMEI 2: <span class="text-dark">{{ item.device_details.imei_2 || '---' }}</span>
                                        </div>
                                        <div class="text-muted fs-12">
                                            OS: <span class="text-dark text-capitalize">{{ item.device_details.os_type || '---' }}</span> | 
                                            Type: <span class="text-dark text-capitalize">{{ item.device_details.device_type || '---' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </td>

                            <!-- Customer -->
                            <td>
                                <router-link 
                                    :to="`/customer/customer-details/${item.customer.id}`"
                                    class="text-primary fw-medium fs-13"
                                >
                                    {{ item.customer.name }}
                                </router-link>
                                <div class="text-muted fs-12">{{ item.customer.ic || '---' }}</div>
                            </td>

                            <!-- Contract & Order -->
                            <td>
                                <div class="mb-1">
                                    <router-link :to="`/contracts/customer-contract-details/${item.contract.id}`" class="text-primary fw-medium fs-13">
                                        {{ item.contract.number }}
                                    </router-link>
                                </div>
                                <router-link :to="`/orders/customer-order-details/${item.order.id}`" class="text-primary fw-medium fs-13">
                                    {{ item.order.number }}
                                </router-link>
                            </td>   

                            <!-- MDM Status & Health -->
                            <td>
                                <div class="d-flex gap-2 mb-2 flex-wrap">
                                    <span :class="['badge', mdmStateClass(item.mdm_details.status)]">
                                        {{ item.mdm_details.status_description }}
                                    </span>
                                    <span :class="['badge', mdmProfileStatusClass(item.mdm_details.mdm_profile_status)]">
                                        {{ mdmProfileStatusLabel(item.mdm_details.mdm_profile_status) }}
                                    </span>
                                </div>
                                <div class="text-muted fs-11">
                                    <span class="fw-medium text-dark">Enrolled:</span> {{ item.mdm_details.enrolled_at || '---' }}
                                </div>

                                <!-- Integrated History Stats (Merged as requested) -->
                                <div class="mt-2 d-flex align-items-center gap-2 border-top pt-1 opacity-75">
                                    <div class="fs-10 text-uppercase fw-bold text-muted">History:</div>
                                    <div class="fs-11">
                                        <span class="text-warning fw-medium">Warn:</span> {{ item.command_statistics?.warning_count || 0 }}
                                    </div>
                                    <div class="vr" style="height: 10px;"></div>
                                    <div class="fs-11">
                                        <span class="text-danger fw-medium">Lost:</span> {{ item.command_statistics?.lost_mode_enabled_count || 0 }}
                                    </div>
                                    <div class="vr" style="height: 10px;"></div>
                                    <div class="fs-11">
                                        <span class="text-info fw-medium">Disable:</span> {{ item.command_statistics?.lost_mode_disabled_count || 0 }}
                                    </div>
                                </div>
                            </td>                                          
                            

                            <!-- Timestamps -->
                            <td>
                                <div class="text-muted fs-11 mb-1">
                                    <i class="ri-add-circle-line align-middle me-1"></i>{{ item.system_logs.created_at }}
                                </div>
                                <div class="text-muted fs-11">
                                    <i class="ri-refresh-line align-middle me-1"></i>{{ item.system_logs.updated_at }}
                                </div>
                            </td>                                         
                        </tr>
                    </tbody>
                </DataTable>
            </div>
        </div>

        <!-- Export History Modal -->
        <div v-if="showExportModal" class="modal-backdrop fade show"></div>
        <div 
            class="modal fade" 
            :class="{ show: showExportModal }" 
            :style="{ display: showExportModal ? 'block' : 'none' }"
            tabindex="-1" 
            role="dialog"
        >
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content border-0 shadow">
                    <div class="modal-header bg-soft-success py-3">
                        <h5 class="modal-title fw-bold text-success">
                            <i class="ri-file-excel-2-line me-2 align-bottom"></i> Export MDM Reports
                        </h5>
                        <button type="button" class="btn-close" @click="closeExportModal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body p-4">
                        <div class="mb-0">
                            <label class="form-label text-muted fs-12 text-uppercase fw-bold">Select Date Range</label>
                            <flat-pickr 
                                v-model="exportFilters.dateRange" 
                                :config="flatpickrConfig" 
                                class="form-control" 
                                placeholder="Select Start & End Date"
                            ></flat-pickr>
                            <div class="mt-3 fs-11 text-muted border-top pt-2">
                                <div class="d-flex align-items-center mb-1">
                                    <i class="ri-device-line me-2 text-success"></i>
                                    <span><strong>Devices Export:</strong> follows enrollment date.</span>
                                </div>
                                <div class="d-flex align-items-center">
                                    <i class="ri-history-line me-2 text-info"></i>
                                    <span><strong>Actions History:</strong> follows requested date (requested_at).</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer bg-light p-3">
                        <button type="button" class="btn btn-ghost-danger btn-sm px-3 mr-auto" @click="closeExportModal">Cancel</button>
                        
                        <button 
                            type="button" 
                            class="btn btn-info btn-sm px-4" 
                            @click="handleExportHistorical"
                            :disabled="isButtonDisabled"
                        >
                            <i class="ri-history-line me-1 align-bottom"></i> Export Actions History
                        </button>
                        
                        <button 
                            type="button" 
                            class="btn btn-success btn-sm px-4" 
                            @click="handleExportDevices"
                            :disabled="isButtonDisabled"
                        >
                            <span v-if="isButtonDisabled">
                                <span class="spinner-border spinner-border-sm me-1" role="status"></span> Generating...
                            </span>
                            <span v-else>
                                <i class="ri-file-download-line me-1 align-bottom"></i> Export Devices
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
